import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { getWideViewOwnershipTableColumns } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Ownership/WideViewOwnershipTable/wideViewOwnershipTableColumns';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useMantineTheme } from '@mantine/core';
import { useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HoldingRes, HoldingSecurityRes } from '@dealstack/shared/lib/generated';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getPersonSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { useMantineTableDataOperationsFromUrlParamState } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState.ts';
import { getFilterableColumnIds } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';

export type WideViewOwnershipTableItemData = HoldingRes & {
	holderId: string;
	totalAcrossAllSecurities?: number;
	subRows: HoldingRes[];
};

export const useWideViewOwnershipTableController = ({
	showSummarized,
	showPercentages,
	isOwnership = [],
}: {
	showSummarized: boolean;
	showPercentages: boolean;
	isOwnership: HoldingRes[];
}) => {
	const tCapManage = useTranslationCapManage();
	const theme = useMantineTheme();
	const { selectedEntityId } = useContext(CapManageControllerContext);

	const getSecuritiesRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
				personId: selectedEntityId,
			}),
		],
		queryFn: ({ signal }) =>
			getPersonSecurities(
				formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
					personId: selectedEntityId,
				}),
				undefined,
				signal
			),
		enabled: !!selectedEntityId,
	});

	useEffect(() => {
		if (selectedEntityId) {
			void getSecuritiesRequest.refetch();
		}
	}, [selectedEntityId]);

	const columns = useMemo(() => {
		const securities = getSecuritiesRequest.data?.securities || [];
		const columns = getWideViewOwnershipTableColumns(
			securities,
			tCapManage,
			showSummarized,
			showPercentages,
			theme
		);

		return columns;
	}, [getSecuritiesRequest.data?.securities, tCapManage, showSummarized, showPercentages, theme]);

	const mantineTableDataOperations = useMantineTableDataOperationsFromUrlParamState();
	const { setColumnFilters } = mantineTableDataOperations;

	useEffect(() => {
		const supportedFilterIds = new Set(getFilterableColumnIds(columns));

		setColumnFilters((currentFilters) =>
			currentFilters.some((filter) => !supportedFilterIds.has(filter.id))
				? currentFilters.filter((filter) => supportedFilterIds.has(filter.id))
				: currentFilters
		);
	}, [columns, setColumnFilters]);

	const wideViewOwnershipData: WideViewOwnershipTableItemData[] = useMemo(() => {
		const groupedHoldings: Record<string, HoldingRes[]> = {};

		isOwnership.forEach((holding) => {
			if (!groupedHoldings[holding.holderId]) {
				groupedHoldings[holding.holderId] = [];
			}
			groupedHoldings[holding.holderId].push(holding);
		});

		const aggregateHoldingsSecurities = (
			holdingsToAggregate: HoldingRes[]
		): HoldingSecurityRes[] => {
			const securities: HoldingSecurityRes[] = [];
			holdingsToAggregate.forEach((holdingToAggregate) => {
				holdingToAggregate.securities.forEach((security) => {
					const existingSecurity = securities.find(
						(s) => s.securityId === security.securityId
					);
					if (existingSecurity) {
						existingSecurity.shareHolding.unitsAmount =
							(existingSecurity.shareHolding.unitsAmount || 0) +
							(security.shareHolding.unitsAmount || 0);
						existingSecurity.shareHolding.unitsPercentage =
							(existingSecurity.shareHolding.unitsPercentage || 0) +
							(security.shareHolding.unitsPercentage || 0);
						existingSecurity.shareHolding.votesAmount =
							(existingSecurity.shareHolding.votesAmount || 0) +
							(security.shareHolding.votesAmount || 0);
						existingSecurity.shareHolding.votesPercentage =
							(existingSecurity.shareHolding.votesPercentage || 0) +
							(security.shareHolding.votesPercentage || 0);
					} else {
						securities.push({
							...security,
							shareHolding: { ...security.shareHolding },
						});
					}
				});
			});
			return securities;
		};

		return Object.entries(groupedHoldings).map(([holderId, holdings]) => ({
			holderId,
			totalAcrossAllSecurities: holdings[0].totalAcrossAllSecurities,
			securities: aggregateHoldingsSecurities(holdings),
			holderName: holdings[0].holderName || '',
			subRows: holdings,
		}));
	}, [isOwnership, showPercentages]);

	return {
		wideViewOwnershipData,
		columns,
		mantineTableDataOperations,
		isSecuritiesLoading: getSecuritiesRequest.isLoading,
	};
};
